<script setup>
import { Head, Link } from '@inertiajs/vue3';

defineProps({
    canLogin: {
        type: Boolean,
    },
    canPatientRegister: {
        type: Boolean,
    },
    clinicSettings: {
        type: Object,
        required: true,
    },
});
</script>

<template>
    <Head :title="`Welcome - ${clinicSettings.name} Management System`" />

    <div class="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        <!-- Navigation Header -->
        <header class="bg-white shadow-lg">
            <div class="max-w-7xl mx-auto">
                <div class="flex items-center py-6 pl-4 sm:pl-6 lg:pl-8">
                    <div class="flex items-center">
                        <div class="flex-shrink-0">
                            <!-- Clinic Logo -->
                            <div v-if="clinicSettings.logo" class="h-12 w-12 rounded-lg overflow-hidden">
                                <img
                                    :src="`/storage/${clinicSettings.logo}`"
                                    :alt="`${clinicSettings.name} Logo`"
                                    class="h-full w-full object-contain"
                                />
                            </div>
                            <!-- Fallback Icon if no logo -->
                            <div v-else class="h-12 w-12 bg-blue-600 rounded-lg flex items-center justify-center">
                                <svg class="h-8 w-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547A1.5 1.5 0 004 16.5v4.268a2 2 0 00.586 1.414l.636.636a2 2 0 001.414.586h10.729a2 2 0 001.414-.586l.636-.636A2 2 0 0020 20.732V16.5a1.5 1.5 0 00-.572-1.072z"></path>
                                </svg>
                            </div>
                        </div>
                        <div class="ml-4">
                            <h1 class="text-2xl font-bold text-gray-900">{{ clinicSettings.name }}</h1>
                            <p class="text-sm text-gray-600">{{ clinicSettings.address }}</p>
                        </div>
                    </div>

                    <!-- Login Button -->
                    <div v-if="canLogin" class="ml-auto">
                        <Link
                            :href="route('login')"
                            class="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-md text-sm font-medium transition-colors duration-200 shadow-sm"
                        >
                            Staff Login
                        </Link>
                    </div>
                </div>
            </div>
        </header>

        <!-- Hero Section -->
        <main class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div class="text-center">
                <h2 class="text-4xl font-extrabold text-gray-900 sm:text-5xl md:text-6xl">
                    Professional Laboratory
                    <span class="text-blue-600">Management System</span>
                </h2>
                <p class="mt-6 max-w-3xl mx-auto text-xl text-gray-600 leading-relaxed">
                    Streamline your diagnostic clinic operations with our comprehensive laboratory information management system.
                    From patient registration to result reporting, we've got you covered.
                </p>

                <div class="mt-10 flex flex-col items-center space-y-6">
                    <!-- Patient Registration Section -->
                    <div v-if="canPatientRegister" class="text-center">
                        <p class="text-lg text-gray-700 mb-4 font-medium">New Patient?</p>
                        <Link
                            :href="route('patient.registration.index')"
                            class="bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-lg text-lg font-medium shadow-lg transition-all duration-200 transform hover:scale-105 inline-flex items-center"
                        >
                            <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"></path>
                            </svg>
                            Register as New Patient
                        </Link>
                        <p class="text-sm text-gray-500 mt-3 max-w-md">
                            Pre-register your information to speed up your first visit to our clinic.
                        </p>
                    </div>

                    <!-- Staff Login Section -->
                    <div class="text-center">
                        <p class="text-lg text-gray-700 mb-4 font-medium">Clinic Staff?</p>
                        <div class="flex justify-center space-x-6">
                            <Link
                                v-if="canLogin"
                                :href="route('login')"
                                class="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg text-lg font-medium shadow-lg transition-all duration-200 transform hover:scale-105"
                            >
                                Staff Login
                            </Link>
                            <a href="#features" class="border border-blue-600 text-blue-600 hover:bg-blue-50 px-8 py-3 rounded-lg text-lg font-medium transition-colors duration-200">
                                Learn More
                            </a>
                        </div>
                        <p class="text-sm text-gray-500 mt-3 max-w-md">
                            Staff accounts are created by system administrators. Contact your clinic administrator for access.
                        </p>
                    </div>
                </div>
            </div>

            <!-- Features Section -->
            <div id="features" class="mt-24">
                <div class="text-center">
                    <h3 class="text-3xl font-bold text-gray-900 mb-4">Comprehensive Solution</h3>
                    <p class="text-xl text-gray-600 mb-16">Everything you need to manage a modern diagnostic clinic</p>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    <!-- Patient Management -->
                    <div class="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow duration-300">
                        <div class="h-12 w-12 bg-green-100 rounded-lg flex items-center justify-center mb-6">
                            <svg class="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                            </svg>
                        </div>
                        <h4 class="text-xl font-semibold text-gray-900 mb-3">Patient Management</h4>
                        <p class="text-gray-600 leading-relaxed">
                            Comprehensive patient registration, demographics, and medical history tracking with Philippine healthcare standards compliance.
                        </p>
                    </div>

                    <!-- Test Orders -->
                    <div class="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow duration-300">
                        <div class="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center mb-6">
                            <svg class="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                            </svg>
                        </div>
                        <h4 class="text-xl font-semibold text-gray-900 mb-3">Test Order Management</h4>
                        <p class="text-gray-600 leading-relaxed">
                            Efficient test ordering from external prescriptions, priority handling, and complete order tracking throughout the process.
                        </p>
                    </div>

                    <!-- Laboratory Processing -->
                    <div class="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow duration-300">
                        <div class="h-12 w-12 bg-purple-100 rounded-lg flex items-center justify-center mb-6">
                            <svg class="h-6 w-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547A1.5 1.5 0 004 16.5v4.268a2 2 0 00.586 1.414l.636.636a2 2 0 001.414.586h10.729a2 2 0 001.414-.586l.636-.636A2 2 0 0020 20.732V16.5a1.5 1.5 0 00-.572-1.072z"></path>
                            </svg>
                        </div>
                        <h4 class="text-xl font-semibold text-gray-900 mb-3">Laboratory Processing</h4>
                        <p class="text-gray-600 leading-relaxed">
                            Sample collection, processing workflows, result entry with validation, and quality control measures.
                        </p>
                    </div>

                    <!-- Result Management -->
                    <div class="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow duration-300">
                        <div class="h-12 w-12 bg-yellow-100 rounded-lg flex items-center justify-center mb-6">
                            <svg class="h-6 w-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z"></path>
                            </svg>
                        </div>
                        <h4 class="text-xl font-semibold text-gray-900 mb-3">Result Management</h4>
                        <p class="text-gray-600 leading-relaxed">
                            Parameter-based result entry, critical value detection, multi-stage approval workflows, and result amendments.
                        </p>
                    </div>

                    <!-- Reporting System -->
                    <div class="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow duration-300">
                        <div class="h-12 w-12 bg-red-100 rounded-lg flex items-center justify-center mb-6">
                            <svg class="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                            </svg>
                        </div>
                        <h4 class="text-xl font-semibold text-gray-900 mb-3">Professional Reporting</h4>
                        <p class="text-gray-600 leading-relaxed">
                            Generate professional lab reports, summary statistics, patient history trends, and critical value alerts.
                        </p>
                    </div>

                    <!-- Security & Compliance -->
                    <div class="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow duration-300">
                        <div class="h-12 w-12 bg-indigo-100 rounded-lg flex items-center justify-center mb-6">
                            <svg class="h-6 w-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
                            </svg>
                        </div>
                        <h4 class="text-xl font-semibold text-gray-900 mb-3">Security & Compliance</h4>
                        <p class="text-gray-600 leading-relaxed">
                            Role-based access control, comprehensive audit logging, and compliance with medical data protection standards.
                        </p>
                    </div>
                </div>
            </div>

            <!-- User Roles Section -->
            <div class="mt-24">
                <div class="text-center">
                    <h3 class="text-3xl font-bold text-gray-900 mb-4">Multi-Role Access</h3>
                    <p class="text-xl text-gray-600 mb-16">Designed for different healthcare professionals</p>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <!-- Reception -->
                    <div class="bg-white rounded-lg shadow-md p-6 text-center hover:shadow-lg transition-shadow duration-200">
                        <div class="h-16 w-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <svg class="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
                            </svg>
                        </div>
                        <h4 class="text-lg font-semibold text-gray-900 mb-2">Reception Staff</h4>
                        <p class="text-sm text-gray-600">Patient registration, test ordering, billing, and appointment management</p>
                    </div>

                    <!-- Lab Tech -->
                    <div class="bg-white rounded-lg shadow-md p-6 text-center hover:shadow-lg transition-shadow duration-200">
                        <div class="h-16 w-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <svg class="h-8 w-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547A1.5 1.5 0 004 16.5v4.268a2 2 0 00.586 1.414l.636.636a2 2 0 001.414.586h10.729a2 2 0 001.414-.586l.636-.636A2 2 0 0020 20.732V16.5a1.5 1.5 0 00-.572-1.072z"></path>
                            </svg>
                        </div>
                        <h4 class="text-lg font-semibold text-gray-900 mb-2">Lab Technician</h4>
                        <p class="text-sm text-gray-600">Sample processing, result entry, quality control, and report generation</p>
                    </div>

                    <!-- Admin -->
                    <div class="bg-white rounded-lg shadow-md p-6 text-center hover:shadow-lg transition-shadow duration-200">
                        <div class="h-16 w-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <svg class="h-8 w-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path>
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                            </svg>
                        </div>
                        <h4 class="text-lg font-semibold text-gray-900 mb-2">Administrator</h4>
                        <p class="text-sm text-gray-600">System management, user administration, test configuration, and analytics</p>
                    </div>

                    <!-- Patient -->
                    <div class="bg-white rounded-lg shadow-md p-6 text-center hover:shadow-lg transition-shadow duration-200">
                        <div class="h-16 w-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <svg class="h-8 w-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                            </svg>
                        </div>
                        <h4 class="text-lg font-semibold text-gray-900 mb-2">Patients</h4>
                        <p class="text-sm text-gray-600">Online result viewing, report downloads, and appointment scheduling (Future)</p>
                    </div>
                </div>
            </div>
        </main>

        <!-- Footer -->
        <footer class="bg-white border-t border-gray-200 mt-24">
            <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
                    <div>
                        <div class="flex items-center">
                            <!-- Footer Logo -->
                            <div v-if="clinicSettings.logo" class="h-8 w-8 rounded-lg overflow-hidden">
                                <img
                                    :src="`/storage/${clinicSettings.logo}`"
                                    :alt="`${clinicSettings.name} Logo`"
                                    class="h-full w-full object-contain"
                                />
                            </div>
                            <!-- Fallback Icon if no logo -->
                            <div v-else class="h-8 w-8 bg-blue-600 rounded-lg flex items-center justify-center">
                                <svg class="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547A1.5 1.5 0 004 16.5v4.268a2 2 0 00.586 1.414l.636.636a2 2 0 001.414.586h10.729a2 2 0 001.414-.586l.636.636A2 2 0 0020 20.732V16.5a1.5 1.5 0 00-.572-1.072z"></path>
                                </svg>
                            </div>
                            <span class="ml-2 text-lg font-semibold text-gray-900">{{ clinicSettings.name }}</span>
                        </div>
                        <p class="mt-4 text-gray-600">
                            Professional laboratory information management system designed for modern diagnostic clinics and outpatient facilities.
                        </p>
                    </div>

                    <div>
                        <h3 class="text-sm font-semibold text-gray-900 tracking-wider uppercase">Features</h3>
                        <ul class="mt-4 space-y-2">
                            <li><span class="text-gray-600">Patient Management</span></li>
                            <li><span class="text-gray-600">Test Order Processing</span></li>
                            <li><span class="text-gray-600">Laboratory Workflows</span></li>
                            <li><span class="text-gray-600">Result Management</span></li>
                            <li><span class="text-gray-600">Professional Reporting</span></li>
                        </ul>
                    </div>

                    <div>
                        <h3 class="text-sm font-semibold text-gray-900 tracking-wider uppercase">Built With</h3>
                        <ul class="mt-4 space-y-2">
                            <li><span class="text-gray-600">Laravel Framework</span></li>
                            <li><span class="text-gray-600">Vue.js & Inertia.js</span></li>
                            <li><span class="text-gray-600">TailwindCSS</span></li>
                            <li><span class="text-gray-600">Professional Standards</span></li>
                            <li><span class="text-gray-600">Secure & Compliant</span></li>
                        </ul>
                    </div>
                </div>

                <div class="mt-8 pt-8 border-t border-gray-200">
                    <p class="text-center text-gray-400 text-sm">
                        © {{ new Date().getFullYear() }} {{ clinicSettings.name }} Management System. Professional healthcare technology solutions.
                    </p>
                </div>
            </div>
        </footer>
    </div>
</template>

<style scoped>
/* Custom smooth scrolling */
html {
    scroll-behavior: smooth;
}

/* Add any additional custom styles here */
</style>
